import React, { useState, useEffect } from "react";
import { Box, Grid, Typography, Paper } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import CustomButton from "../../component/ui/FormFields/Button";
import {
  UNAUTHORIZE_BOOKING_AIRPORT,
  UNAUTHORIZE_BOOKING_AIRPORT_DETAILS,
  LOGIN,
} from "../../component/common/Routes/AppRoutes";
import AtithiLogo from "../../assets/Atithiseva-Logo-Final-1024x298.png";
import { fetchAllAirportRequest } from "../../redux/Airports/actions/airportActions";
import { instantBookingTokenGeneratorRequest } from "../../redux/Bookings/BookingAction";
import Loading from "../../component/ui/Loading";
import CryptoJS from "crypto-js";

const AirportBookingForm1 = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);

  const SECRET_KEY = "843567893696976453275974432697R634976";

  // Redux selectors
  const token = useSelector(
    (state) => state.booking?.instantBooking?.data || []
  );
  const airport = useSelector(
    (state) => state.airport.airportsList?.data || []
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      airport_type: "",
      airport: "",
      type_of_class: "",
      arrival_departure_type: "",
      service_type: "",
    },
    mode: "onChange",
  });

  const formValue = watch();

  // Initialize token and fetch data
  useEffect(() => {
    dispatch(instantBookingTokenGeneratorRequest());
  }, [dispatch]);

  // Store token and fetch airports when token is available
  useEffect(() => {
    if (token.length > 0) {
      const encryptedAccessToken = CryptoJS.AES.encrypt(
        token,
        SECRET_KEY
      ).toString();

      // Store in sessionStorage
      sessionStorage.setItem("newBookingAccessToken", encryptedAccessToken);

      // Broadcast to other new-booking tabs
      localStorage.setItem("newBookingAccessToken", encryptedAccessToken);

      dispatch(fetchAllAirportRequest());
      setIsLoading(false);
    }
  }, [token, dispatch]);

  // Filter airports based on status
  const filteredAirports = airport.filter((airport) => {
    return airport?.status === 1 && airport?.approoved_status === 1;
  });

  // Get airport category for selected airport
  const selectedAirport = filteredAirports.find(
    (airport) => airport?.id === formValue?.airport?.value
  );
  const airportCategory = selectedAirport?.airport_type;

  // Airport type options
  const airportTypes = [
    {
      label: "Airports In India",
      value: "Domestic",
    },
    {
      label: "Airports outside India",
      value: "International",
    },
  ];

  // Travel class options
  const travelClassTypes = [
    {
      label: "First class",
      value: "First class",
    },
    {
      label: "Business class",
      value: "Business class",
    },
    {
      label: "Premium Economy",
      value: "Premium Economy",
    },
    {
      label: "Economy",
      value: "Economy",
    },
  ];

  // Filter airports based on selected airport type
  const orderedAirports = filteredAirports
    .map((airport) => ({
      value: airport.id,
      label: airport.name,
      country: airport.country_id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const airports = orderedAirports.filter((airport) => {
    return formValue?.airport_type?.value === "Domestic"
      ? airport?.country === 101
      : formValue?.airport_type?.value === "International"
      ? airport?.country !== 101
      : true;
  });

  // Service type options based on airport category
  const getServiceTypeOptions = () => {
    return formValue.airport_type?.value === "International"
      ? [{ value: "International", label: "International" }]
      : airportCategory === "International" || airportCategory === "Both"
      ? [
          { value: "Domestic", label: "Domestic" },
          { value: "International", label: "International" },
        ]
      : [{ value: "Domestic", label: "Domestic" }];
  };

  // Arrival/Departure type options based on airport category
  const getArrivalDepartureOptions = () => {
    if (airportCategory === "International" || airportCategory === "Both") {
      return [
        { value: "Arrival", label: "Arrival" },
        { value: "Departure", label: "Departure" },
        { value: "Transit", label: "Transit" },
      ];
    } else {
      return [
        { value: "Arrival", label: "Arrival" },
        { value: "Departure", label: "Departure" },
      ];
    }
  };

  // Form fields configuration
  const fields = [
    {
      name: "airport_type",
      label: "Airport Type *",
      rules: { required: "Select Airport is required" },
      options: airportTypes,
      placeholder: "Select Airport type",
      sm: 6,
    },
    {
      name: "airport",
      label: "Airport *",
      rules: { required: "Airport is required" },
      options: airports,
      placeholder: "Select Airport",
      sm: 6,
    },
    {
      name: "type_of_class",
      label: "Travel Class *",
      rules: { required: "Select Travel Class is required" },
      options: travelClassTypes,
      placeholder: "Select Travel class type",
      sm: 6,
    },
    {
      name: "arrival_departure_type",
      label: "Arrival/Departure *",
      rules: { required: "Arrival/Departure type is required" },
      options: getArrivalDepartureOptions(),
      placeholder: "Select Arrival/Departure Type",
      sm: 6,
    },
    // Conditional service type field
    ...(formValue?.arrival_departure_type?.value === "Transit"
      ? []
      : [
          {
            name: "service_type",
            label: "Service Type *",
            rules: { required: "Service type is required" },
            options: getServiceTypeOptions(),
            placeholder: "Select Service Type",
            sm: 6,
          },
        ]),
  ];

  const onSubmit = (data) => {
    const form1Value = {
      airport_type: data.airport_type,
      airport: data.airport,
      type_of_class: data.type_of_class,
      arrival_departure_type: data.arrival_departure_type,
      service_type: data.service_type,
      // Add empty defaults for fields that will be filled in next form
      customer: "",
      arrival_plan: "",
      departure_plan: "",
      transit_service: [],
      arrival_city: "",
      departure_city: "",
      departure_date: 0,
      departure_time: "",
      arrival_date: 0,
      arrival_time: "",
      arrival_airline: "",
      departure_airline: "",
      arrival_flight_number: "",
      departure_flight_number: "",
      arival_pnr_by_customer: "",
      departure_pnr_by_customer: "",
      transit_plan: "",
    };

    // Generate a short unique ID (8 characters)
    const formId =
      Date.now().toString(36) + Math.random().toString(36).substr(2, 5);

    // Store in sessionStorage
    sessionStorage.setItem(
      `booking_form_${formId}`,
      JSON.stringify(form1Value)
    );

    // Short URL with just the ID
    const url = `${UNAUTHORIZE_BOOKING_AIRPORT}?id=${formId}`;
    navigate(url);
  };

  if (isLoading) {
    return <Loading text="Loading..." />;
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f9f9f9" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 4,
          py: 1,
          backgroundColor: "#fff",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
          mb: 4,
        }}
      >
        <img src={AtithiLogo} alt="Atithiseva Logo" style={{ height: "45px" }} />
        <CustomButton
          variant="contained"
          className="orange-button"
          text="BACK TO LOGIN"
          onClick={() => navigate(LOGIN)}
        />
      </Box>

      <Box className="add_airport_details_heading booking-wrapper" sx={{ px: 4 }}>
        <Typography variant="h5" className="form-heading">
          Book Now
        </Typography>
        <Box className="form-margin">
          <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            {fields.map((field, index) => (
              <Grid item xs={12} sm={field.sm} key={index}>
                <ControlledTextInputField
                  name={field.name}
                  control={control}
                  rules={field.rules}
                  label={field.label}
                  placeholder={field.placeholder}
                  options={field.options}
                  getOptionLabel={(option) => option.label}
                  error={!!errors[field.name]}
                  helperText={errors[field.name]?.message}
                />
              </Grid>
            ))}
            <Grid item xs={12} className="button-container" sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
              <CustomButton
                variant="contained"
                className="orange-button"
                text="NEXT"
                type="submit"
              />
            </Grid>
          </Grid>
        </form>
      </Box>
      </Box>
    </Box>
  );
};

export default AirportBookingForm1;
